// Audit log — every run, every embedding spend, every governance review.
//
// Deliberately not scoped to the Playground. The runs worth auditing are the
// ones a customer's application made through /api/v1; the Playground's own
// "Recent runs" card is a convenience for the turn you just ran, not this.
//
// Three tabs because there are three kinds of spend a tenant pays for, and
// until now only the first was visible:
//   Runs        — agent runs, with LLM and embedding cost side by side and a
//                 badge when the semantic cache answered.
//   Embeddings  — every embeddings request: KB questions, KB tools inside
//                 runs, ingestion, cache lookups, and /api/v1/embeddings.
//   Governance  — agent-config PR reviews and what the analyzer model cost,
//                 plus who changed agent config and when.
//
// The run detail panel exists so an operator never has to open the model
// provider's dashboard to answer "what was actually sent" — the terminal
// message list is already stored on every run, tool calls and their returned
// payloads included.

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ExternalLink, Zap } from "lucide-react";
import * as React from "react";

import { AccuracyVerdict } from "@/components/evaluations/accuracy";
import { PageHeader } from "@/components/layout-bits";
import { Link } from "@/components/ui/link";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  selectClass,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getAuditRun,
  listAuditEmbeddings,
  listAuditGovernance,
  listAuditRuns,
  listAuditToolLlm,
  listGovernanceActivity,
} from "@/lib/api/endpoints";
import { formatTokens, formatUsd, sumCosts } from "@/lib/format-usage";
import { useKnowledgeBases, useMyAgents } from "@/lib/query/hooks";
import type {
  AuditMessage,
  AuditRunRow,
  CacheDetail,
  EmbeddingUsageRow,
  GovernanceAuditRow,
  LlmUsageRow,
  UsageTotals,
} from "@/types/api";

const PAGE_SIZE = 25;

const SOURCE_LABEL: Record<string, string> = {
  kb_query: "KB console",
  kb_tool: "KB tool (run)",
  kb_ingest: "KB ingest",
  semantic_cache: "Semantic cache",
  api: "API /v1/embeddings",
  model_test: "Connection test",
};

const LLM_SOURCE_LABEL: Record<string, string> = {
  kb_tool: "KB tool (run)",
  kb_query: "KB console",
  model_test: "Connection test",
  accuracy_judge: "Accuracy judge",
  security_review: "Security AI review",
  guardrail: "Guardrail check",
};

/** Everything a run paid for: its own LLM, its tools' LLM, its embeddings. */
const runCost = (r: AuditRunRow) => sumCosts(r.cost_usd, r.tool_llm_cost_usd, r.embedding_cost_usd);

export default function AuditLogsPage() {
  return (
    <>
      <PageHeader
        title="Audit logs"
        description="Every run, embedding call and agent-config review this tenant has — from your applications and the console — with the tokens and cost behind each."
      />
      {/* Radix unmounts inactive tabs, so each tab's queries run only when it
          is opened. */}
      <Tabs defaultValue="runs">
        <TabsList className="mb-4">
          <TabsTrigger value="runs">Runs</TabsTrigger>
          <TabsTrigger value="embeddings">Embeddings</TabsTrigger>
          <TabsTrigger value="tool-llm">Model calls</TabsTrigger>
          <TabsTrigger value="governance">Governance</TabsTrigger>
        </TabsList>
        <TabsContent value="runs">
          <RunsTab />
        </TabsContent>
        <TabsContent value="embeddings">
          <EmbeddingsTab />
        </TabsContent>
        <TabsContent value="tool-llm">
          <ToolLlmTab />
        </TabsContent>
        <TabsContent value="governance">
          <GovernanceTab />
        </TabsContent>
      </Tabs>
    </>
  );
}

// ---- shared bits ------------------------------------------------------------

function Filter({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-[10rem] space-y-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <Filter label={label}>
      <select className={selectClass} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Filter>
  );
}

function Pager({
  offset,
  total,
  onChange,
}: {
  offset: number;
  total: number;
  onChange: (offset: number) => void;
}) {
  return (
    <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
      <span>
        {total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
      </span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={offset === 0}
          onClick={() => onChange(Math.max(0, offset - PAGE_SIZE))}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={offset + PAGE_SIZE >= total}
          onClick={() => onChange(offset + PAGE_SIZE)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

/** Totals for everything matching the filters — not just the visible page. */
function TotalsBar({ totals, requestsLabel }: { totals: UsageTotals; requestsLabel: string }) {
  return (
    <div className="mb-4 flex flex-wrap gap-x-8 gap-y-1 rounded-md border border-border bg-muted/30 px-4 py-3 text-sm">
      <span>
        <span className="text-muted-foreground">{requestsLabel} </span>
        <span className="font-medium tabular-nums">{totals.requests.toLocaleString()}</span>
      </span>
      <span>
        <span className="text-muted-foreground">Tokens </span>
        <span className="font-medium tabular-nums">{formatTokens(totals.tokens)}</span>
      </span>
      <span>
        <span className="text-muted-foreground">Cost </span>
        <span className="font-medium tabular-nums">{formatUsd(totals.cost_usd)}</span>
      </span>
    </div>
  );
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : "—");

// ---- Runs --------------------------------------------------------------------

function RunsTab() {
  const [agentSlug, setAgentSlug] = React.useState("");
  const [runKind, setRunKind] = React.useState("");
  const [source, setSource] = React.useState("");
  const [outcome, setOutcome] = React.useState("");
  const [offset, setOffset] = React.useState(0);
  const [openRun, setOpenRun] = React.useState<string | null>(null);

  const agents = useMyAgents();

  // Every filter change resets the page. Staying on page 4 of a different
  // result set shows an empty table for no visible reason.
  //
  // Done in the setter rather than an effect watching the filters: an effect
  // that calls setOffset is one render away from an update loop, and this is
  // a direct consequence of the click, not something to reconcile afterwards.
  const changeFilter = (set: (v: string) => void) => (v: string) => {
    set(v);
    setOffset(0);
  };

  const runs = useQuery({
    queryKey: ["audit", "runs", agentSlug, runKind, source, outcome, offset],
    queryFn: () =>
      listAuditRuns({
        agent_slug: agentSlug || undefined,
        run_kind: runKind || undefined,
        source: source || undefined,
        outcome: outcome || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    // Keeps the table on screen while the next page loads, instead of
    // collapsing to a skeleton on every filter tweak.
    placeholderData: keepPreviousData,
  });

  const total = runs.data?.total ?? 0;
  const rows = runs.data?.runs ?? [];

  return (
    <>
      <Card className="mb-4">
        <CardContent className="flex flex-wrap gap-3 p-4">
          <FilterSelect
            label="Agent"
            value={agentSlug}
            onChange={changeFilter(setAgentSlug)}
            options={[
              { value: "", label: "All agents" },
              ...(agents.data ?? []).map((a) => ({ value: a.slug, label: a.name })),
            ]}
          />
          <FilterSelect
            label="Kind"
            value={runKind}
            onChange={changeFilter(setRunKind)}
            options={[
              { value: "", label: "All" },
              { value: "chat", label: "Chat" },
              { value: "skill", label: "Skill" },
            ]}
          />
          <FilterSelect
            label="Source"
            value={source}
            onChange={changeFilter(setSource)}
            options={[
              { value: "", label: "All" },
              { value: "application", label: "Application" },
              { value: "playground", label: "Playground" },
              { value: "delegated", label: "Called by an agent" },
            ]}
          />
          <FilterSelect
            label="Outcome"
            value={outcome}
            onChange={changeFilter(setOutcome)}
            options={[
              { value: "", label: "All" },
              { value: "success", label: "Success" },
              { value: "blocked", label: "Blocked by a guardrail" },
              { value: "error", label: "Error" },
            ]}
          />
        </CardContent>
      </Card>

      {runs.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : runs.isError ? (
        <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No runs match"
          hint="Every chat and skill invocation lands here — from your applications as well as the Playground."
        />
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <Table>
                <THead>
                  <TR>
                    <TH>Run</TH>
                    <TH>Agent</TH>
                    <TH>Kind</TH>
                    <TH>Source</TH>
                    <TH>Outcome</TH>
                    <TH className="text-right">Tools</TH>
                    <TH className="text-right">LLM tokens</TH>
                    <TH className="text-right">Embeddings</TH>
                    <TH className="text-right">Cost</TH>
                    <TH>Started</TH>
                    <TH />
                  </TR>
                </THead>
                <TBody>
                  {rows.map((r) => (
                    <React.Fragment key={r.run_id}>
                      <TR>
                        <TD className="font-mono text-xs text-muted-foreground">
                          {r.run_id.slice(0, 8)}
                        </TD>
                        <TD>
                          <div className="text-sm">{r.agent_name ?? "—"}</div>
                          <div className="font-mono text-xs text-muted-foreground">
                            {r.agent_version ?? ""}
                          </div>
                        </TD>
                        <TD>
                          <span className="capitalize">{r.run_kind}</span>
                          {r.skill_slug ? (
                            <span className="ml-1 font-mono text-xs text-muted-foreground">
                              {r.skill_slug}
                            </span>
                          ) : null}
                        </TD>
                        <TD>
                          <Badge variant={r.source === "playground" ? "outline" : "default"}>
                            {r.source}
                          </Badge>
                        </TD>
                        <TD>
                          <div className="flex flex-wrap items-center gap-1">
                            <Badge variant={r.outcome === "success" ? "success" : "destructive"}>
                              {r.outcome ?? r.phase}
                            </Badge>
                            {r.cache_hit ? (
                              <Badge
                                variant="outline"
                                className="gap-1"
                                title="Served from the semantic cache — no LLM call"
                              >
                                <Zap className="h-3 w-3" /> cache
                              </Badge>
                            ) : null}
                          </div>
                        </TD>
                        <TD className="text-right tabular-nums">{r.tool_call_count}</TD>
                        <TD className="text-right tabular-nums">
                          <RunLlmTokens row={r} />
                        </TD>
                        <TD className="text-right tabular-nums">
                          {r.embedding_tokens > 0 ? formatTokens(r.embedding_tokens) : "—"}
                          {r.embedding_cost_usd != null ? (
                            <div className="text-xs text-muted-foreground">
                              {formatUsd(r.embedding_cost_usd)}
                            </div>
                          ) : null}
                        </TD>
                        <TD
                          className="text-right tabular-nums"
                          title={`Agent LLM ${formatUsd(r.cost_usd)} · tools & judge ${formatUsd(r.tool_llm_cost_usd)} · embeddings ${formatUsd(r.embedding_cost_usd)}`}
                        >
                          {formatUsd(runCost(r))}
                        </TD>
                        <TD className="whitespace-nowrap text-xs text-muted-foreground">
                          {when(r.started_at)}
                        </TD>
                        <TD>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setOpenRun(openRun === r.run_id ? null : r.run_id)}
                          >
                            {openRun === r.run_id ? "Hide" : "Inspect"}
                          </Button>
                        </TD>
                      </TR>
                      {openRun === r.run_id ? (
                        <TR>
                          <TD colSpan={11} className="bg-muted/30 p-4">
                            <RunDetail runId={r.run_id} row={r} />
                          </TD>
                        </TR>
                      ) : null}
                    </React.Fragment>
                  ))}
                </TBody>
              </Table>
            </CardContent>
          </Card>
          <Pager offset={offset} total={total} onChange={setOffset} />
        </>
      )}
    </>
  );
}

/** The run's LLM tokens — its own loop plus any completion a tool made (a SQL
 * knowledge base writing its query), which is billed to the same run. */
function RunLlmTokens({ row: r }: { row: AuditRunRow }) {
  const toolTokens = r.tool_llm_tokens_prompt + r.tool_llm_tokens_completion;
  if (r.tokens_total == null && toolTokens === 0) return <>—</>;
  const tokensIn = (r.tokens_prompt ?? 0) + r.tool_llm_tokens_prompt;
  const tokensOut = (r.tokens_completion ?? 0) + r.tool_llm_tokens_completion;
  return (
    <>
      {formatTokens((r.tokens_total ?? 0) + toolTokens)}
      <div className="text-xs text-muted-foreground">
        {tokensIn} in / {tokensOut} out
      </div>
      {toolTokens > 0 ? (
        <div
          className="text-xs text-muted-foreground"
          title="Tokens a SQL knowledge base spent writing its query"
        >
          incl. {formatTokens(toolTokens)} tools & judge
        </div>
      ) : null}
    </>
  );
}

/** What the semantic cache decided for a run, in words. */
function CacheVerdict({ detail }: { detail: CacheDetail }) {
  const score = typeof detail.score === "number" ? detail.score.toFixed(3) : null;
  return (
    <div
      className={`rounded-md border p-3 text-sm ${
        detail.hit ? "border-emerald-500/40 bg-emerald-500/10" : "border-border bg-muted/30"
      }`}
    >
      <div className="flex items-center gap-1.5 font-medium">
        <Zap className="h-4 w-4" />
        {detail.hit
          ? "Answered from the semantic cache"
          : detail.error
            ? "Semantic cache unavailable — the model answered"
            : "Semantic cache miss — the model answered"}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        {score
          ? `Similarity ${score} ${detail.hit ? "≥" : "<"} threshold ${detail.threshold ?? "?"}`
          : "No stored answer to compare with yet."}
        {detail.hit && detail.source_run_id
          ? ` · reused the answer from run ${detail.source_run_id.slice(0, 8)}; no LLM call, knowledge-base search or tool calls.`
          : ""}
        {!detail.hit && detail.stored
          ? " · this answer was stored for the next similar question."
          : ""}
      </div>
    </div>
  );
}

/** The conversation behind one run, fetched only when opened. */
function RunDetail({ runId, row }: { runId: string; row: AuditRunRow }) {
  const detail = useQuery({
    queryKey: ["audit", "run", runId],
    queryFn: () => getAuditRun(runId),
  });

  if (detail.isLoading) return <Skeleton className="h-40 w-full" />;
  if (detail.isError)
    return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;

  const d = detail.data!;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
        <span>
          Model <span className="font-mono text-foreground">{row.model}</span>
        </span>
        <span>
          Caller <span className="font-mono text-foreground">{row.identity_iss}</span>
          {row.identity_sub ? ` / ${row.identity_sub}` : ""}
        </span>
        <span>Iterations {row.iterations}</span>
        {row.duration_ms != null ? <span>{Math.round(row.duration_ms)} ms</span> : null}
        <span>
          Agent LLM {formatUsd(row.cost_usd)} · tools & judge {formatUsd(row.tool_llm_cost_usd)} ·
          embeddings {formatUsd(row.embedding_cost_usd)}
        </span>
        <span className="font-mono">{row.run_id}</span>
        {row.parent_run_id ? (
          <span>
            Called as a tool by run{" "}
            <span className="font-mono text-foreground">{row.parent_run_id.slice(0, 8)}</span>
          </span>
        ) : null}
      </div>

      {row.failure_message ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
          <div className="font-medium">{row.failure_reason}</div>
          <div className="text-muted-foreground">{row.failure_message}</div>
        </div>
      ) : null}

      {row.cache_detail ? <CacheVerdict detail={row.cache_detail} /> : null}

      {d.accuracy ? <AccuracyVerdict accuracy={d.accuracy} /> : null}

      {d.tool_llm_calls.length > 0 ? (
        <div className="space-y-2">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Tool model calls ({d.tool_llm_calls.length})
          </span>
          <div className="rounded-md border border-border bg-card">
            <ToolLlmTable rows={d.tool_llm_calls} compact />
          </div>
        </div>
      ) : null}

      {d.embedding_calls.length > 0 ? (
        <div className="space-y-2">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Embedding calls ({d.embedding_calls.length})
          </span>
          <div className="rounded-md border border-border bg-card">
            <EmbeddingTable rows={d.embedding_calls} compact />
          </div>
        </div>
      ) : null}

      {d.guardrail_triggers && d.guardrail_triggers.length > 0 ? (
        <div className="space-y-2">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Guardrail triggers ({d.guardrail_triggers.length})
          </span>
          <div className="divide-y divide-border rounded-md border border-border bg-card">
            {d.guardrail_triggers.map((t, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                <Badge variant="outline">{t.stage === "input" ? "Input" : "Output"}</Badge>
                <span className="font-medium">{t.validator}</span>
                <Badge
                  variant={
                    t.action === "warn"
                      ? "outline"
                      : t.action === "redact"
                        ? "default"
                        : "destructive"
                  }
                >
                  {t.action ?? "retry"}
                </Badge>
                <span className="text-muted-foreground">{t.rejection_reason}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {d.child_runs && d.child_runs.length > 0 ? (
        <div className="space-y-2">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Agents called as tools ({d.child_runs.length})
          </span>
          {/* Each child is its own run with its own cost; open one for its
              conversation, and any agents it called in turn. */}
          {d.child_runs.map((c) => (
            <details key={c.run_id} className="rounded-md border border-border bg-card">
              <summary className="flex cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 p-3 text-sm">
                <span className="font-medium">
                  {c.agent_name ?? c.agent_slug ?? "Unknown agent"}
                </span>
                <span className="font-mono text-xs text-muted-foreground">
                  {c.agent_version ?? ""}
                </span>
                <Badge variant={c.outcome === "success" ? "success" : "destructive"}>
                  {c.outcome ?? c.phase}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {c.tokens_total ?? 0} tokens · {formatUsd(c.cost_usd)}
                  {c.duration_ms != null ? ` · ${Math.round(c.duration_ms)} ms` : ""}
                </span>
              </summary>
              <div className="border-t border-border p-3">
                <RunDetail runId={c.run_id} row={c} />
              </div>
            </details>
          ))}
        </div>
      ) : null}

      {d.skill_input ? <Json label="Skill input" value={d.skill_input} /> : null}
      {d.skill_output ? <Json label="Skill output" value={d.skill_output} /> : null}

      <div className="space-y-2">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Messages ({d.messages.length})
        </span>
        {d.messages.map((m, i) => (
          <Message key={i} message={m} />
        ))}
      </div>
    </div>
  );
}

const ROLE_STYLE: Record<string, string> = {
  system: "border-l-muted-foreground/40",
  user: "border-l-primary",
  assistant: "border-l-emerald-500",
  tool: "border-l-amber-500",
};

function Message({ message }: { message: AuditMessage }) {
  const calls = message.tool_calls ?? [];
  return (
    <div className={`border-l-2 pl-3 ${ROLE_STYLE[message.role] ?? "border-l-border"}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {message.role}
        </span>
        {message.name ? (
          <span className="font-mono text-xs text-muted-foreground">{message.name}</span>
        ) : null}
      </div>
      {message.content ? (
        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words text-sm text-foreground">
          {message.content}
        </pre>
      ) : null}
      {calls.map((c) => (
        <div key={c.id} className="mt-1 rounded-md bg-muted/50 p-2">
          <span className="font-mono text-xs text-foreground">{c.name}</span>
          <pre className="mt-1 overflow-x-auto text-xs text-muted-foreground">
            {JSON.stringify(c.arguments, null, 2)}
          </pre>
        </div>
      ))}
    </div>
  );
}

function Json({ label, value }: { label: string; value: Record<string, unknown> }) {
  return (
    <div>
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <pre className="mt-1 overflow-x-auto rounded-md bg-muted/50 p-2 text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

// ---- Embeddings --------------------------------------------------------------

function EmbeddingsTab() {
  const [source, setSource] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [agentSlug, setAgentSlug] = React.useState("");
  const [kbSlug, setKbSlug] = React.useState("");
  const [offset, setOffset] = React.useState(0);

  const agents = useMyAgents();
  const kbs = useKnowledgeBases();

  const changeFilter = (set: (v: string) => void) => (v: string) => {
    set(v);
    setOffset(0);
  };

  const usage = useQuery({
    queryKey: ["audit", "embeddings", source, status, agentSlug, kbSlug, offset],
    queryFn: () =>
      listAuditEmbeddings({
        source: source || undefined,
        status: status || undefined,
        agent_slug: agentSlug || undefined,
        kb_slug: kbSlug || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    placeholderData: keepPreviousData,
  });

  const rows = usage.data?.rows ?? [];

  return (
    <>
      <Card className="mb-4">
        <CardContent className="flex flex-wrap gap-3 p-4">
          <FilterSelect
            label="Source"
            value={source}
            onChange={changeFilter(setSource)}
            options={[
              { value: "", label: "All sources" },
              ...Object.entries(SOURCE_LABEL).map(([value, label]) => ({ value, label })),
            ]}
          />
          <FilterSelect
            label="Status"
            value={status}
            onChange={changeFilter(setStatus)}
            options={[
              { value: "", label: "All" },
              { value: "ok", label: "OK" },
              { value: "error", label: "Error" },
            ]}
          />
          <FilterSelect
            label="Agent"
            value={agentSlug}
            onChange={changeFilter(setAgentSlug)}
            options={[
              { value: "", label: "All agents" },
              ...(agents.data ?? []).map((a) => ({ value: a.slug, label: a.name })),
            ]}
          />
          <FilterSelect
            label="Knowledge base"
            value={kbSlug}
            onChange={changeFilter(setKbSlug)}
            options={[
              { value: "", label: "All knowledge bases" },
              ...(kbs.data ?? []).map((k) => ({ value: k.slug, label: k.name })),
            ]}
          />
        </CardContent>
      </Card>

      {usage.data ? <TotalsBar totals={usage.data.totals} requestsLabel="Requests" /> : null}

      {usage.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : usage.isError ? (
        <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No embedding calls match"
          hint="Knowledge-base questions, KB tools inside runs, ingestion, semantic-cache lookups and /api/v1/embeddings all land here."
        />
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <EmbeddingTable rows={rows} />
            </CardContent>
          </Card>
          <Pager offset={offset} total={usage.data?.total ?? 0} onChange={setOffset} />
        </>
      )}
    </>
  );
}

function EmbeddingTable({
  rows,
  compact = false,
}: {
  rows: EmbeddingUsageRow[];
  compact?: boolean;
}) {
  return (
    <Table>
      <THead>
        <TR>
          <TH>When</TH>
          <TH>Source</TH>
          {compact ? null : <TH>Attributed to</TH>}
          <TH>Model</TH>
          <TH className="text-right">Inputs</TH>
          <TH className="text-right">Tokens</TH>
          <TH className="text-right">Cost</TH>
          <TH className="text-right">Duration</TH>
          <TH>Result</TH>
        </TR>
      </THead>
      <TBody>
        {rows.map((u) => (
          <TR key={u.id}>
            <TD className="whitespace-nowrap text-xs text-muted-foreground">
              {when(u.occurred_at)}
            </TD>
            <TD className="whitespace-nowrap text-sm">{SOURCE_LABEL[u.source] ?? u.source}</TD>
            {compact ? null : (
              <TD className="text-sm">
                <Attribution row={u} />
              </TD>
            )}
            <TD className="font-mono text-xs">{u.model}</TD>
            <TD className="text-right tabular-nums">
              {u.input_count.toLocaleString()}
              {u.request_count > 1 ? (
                <div className="text-xs text-muted-foreground">{u.request_count} requests</div>
              ) : null}
            </TD>
            <TD className="text-right tabular-nums">{formatTokens(u.total_tokens)}</TD>
            <TD className="text-right tabular-nums">{formatUsd(u.cost_usd)}</TD>
            <TD className="text-right text-xs tabular-nums text-muted-foreground">
              {u.duration_ms != null ? `${Math.round(u.duration_ms)} ms` : "—"}
            </TD>
            <TD>
              <UsageResult row={u} />
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

function Attribution({ row }: { row: EmbeddingUsageRow }) {
  if (row.agent_name) {
    return (
      <div>
        <div>{row.agent_name}</div>
        {row.run_id ? (
          <div className="font-mono text-xs text-muted-foreground">
            run {row.run_id.slice(0, 8)}
          </div>
        ) : null}
      </div>
    );
  }
  if (row.knowledge_base_name) return <div>{row.knowledge_base_name}</div>;
  if (row.identity_iss) return <div className="font-mono text-xs">{row.identity_iss}</div>;
  // A connection test names its connection in `detail`, so the receipt still
  // reads correctly after the connection is deleted.
  if (typeof row.detail?.connection === "string") {
    return <div>Connection {row.detail.connection}</div>;
  }
  return <span className="text-muted-foreground">—</span>;
}

// ---- Tool model calls --------------------------------------------------------

/** Completions made outside an agent's loop: a SQL knowledge base writing its
 * query — in a run (billed to that run) or from the KB console — and the Test
 * button on LLM-model connections. */
function ToolLlmTab() {
  const [source, setSource] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [agentSlug, setAgentSlug] = React.useState("");
  const [kbSlug, setKbSlug] = React.useState("");
  const [offset, setOffset] = React.useState(0);

  const agents = useMyAgents();
  const kbs = useKnowledgeBases();

  const changeFilter = (set: (v: string) => void) => (v: string) => {
    set(v);
    setOffset(0);
  };

  const calls = useQuery({
    queryKey: ["audit", "tool-llm", source, status, agentSlug, kbSlug, offset],
    queryFn: () =>
      listAuditToolLlm({
        source: source || undefined,
        status: status || undefined,
        agent_slug: agentSlug || undefined,
        kb_slug: kbSlug || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    placeholderData: keepPreviousData,
  });

  const rows = calls.data?.rows ?? [];

  return (
    <>
      <Card className="mb-4">
        <CardContent className="flex flex-wrap gap-3 p-4">
          <FilterSelect
            label="Source"
            value={source}
            onChange={changeFilter(setSource)}
            options={[
              { value: "", label: "All sources" },
              ...Object.entries(LLM_SOURCE_LABEL).map(([value, label]) => ({ value, label })),
            ]}
          />
          <FilterSelect
            label="Status"
            value={status}
            onChange={changeFilter(setStatus)}
            options={[
              { value: "", label: "All" },
              { value: "ok", label: "OK" },
              { value: "error", label: "Error" },
            ]}
          />
          <FilterSelect
            label="Agent"
            value={agentSlug}
            onChange={changeFilter(setAgentSlug)}
            options={[
              { value: "", label: "All agents" },
              ...(agents.data ?? []).map((a) => ({ value: a.slug, label: a.name })),
            ]}
          />
          <FilterSelect
            label="Knowledge base"
            value={kbSlug}
            onChange={changeFilter(setKbSlug)}
            options={[
              { value: "", label: "All knowledge bases" },
              ...(kbs.data ?? []).map((k) => ({ value: k.slug, label: k.name })),
            ]}
          />
        </CardContent>
      </Card>

      {calls.data ? <TotalsBar totals={calls.data.totals} requestsLabel="Calls" /> : null}

      {calls.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : calls.isError ? (
        <ErrorState error={calls.error} onRetry={() => void calls.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No model calls match"
          hint="Each question a SQL knowledge base answers costs one completion to write the query — from agent runs and from the KB console. LLM-connection tests land here too."
        />
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <ToolLlmTable rows={rows} />
            </CardContent>
          </Card>
          <Pager offset={offset} total={calls.data?.total ?? 0} onChange={setOffset} />
        </>
      )}
    </>
  );
}

function ToolLlmTable({ rows, compact = false }: { rows: LlmUsageRow[]; compact?: boolean }) {
  return (
    <Table>
      <THead>
        <TR>
          <TH>When</TH>
          <TH>Source</TH>
          {compact ? null : <TH>Attributed to</TH>}
          <TH>Knowledge base</TH>
          <TH>Model</TH>
          <TH className="text-right">Tokens</TH>
          <TH className="text-right">Cost</TH>
          <TH className="text-right">Duration</TH>
          <TH>Result</TH>
        </TR>
      </THead>
      <TBody>
        {rows.map((u) => (
          <TR key={u.id}>
            <TD className="whitespace-nowrap text-xs text-muted-foreground">
              {when(u.occurred_at)}
            </TD>
            <TD className="whitespace-nowrap text-sm">{LLM_SOURCE_LABEL[u.source] ?? u.source}</TD>
            {compact ? null : (
              <TD className="text-sm">
                {u.agent_name ? (
                  <div>
                    <div>{u.agent_name}</div>
                    {u.run_id ? (
                      <div className="font-mono text-xs text-muted-foreground">
                        run {u.run_id.slice(0, 8)}
                      </div>
                    ) : null}
                  </div>
                ) : typeof u.detail?.connection === "string" ? (
                  <div>Connection {u.detail.connection}</div>
                ) : (
                  <span className="text-muted-foreground">console</span>
                )}
              </TD>
            )}
            <TD className="text-sm">{u.knowledge_base_name ?? "—"}</TD>
            <TD className="font-mono text-xs">{u.model}</TD>
            <TD className="text-right tabular-nums">
              {formatTokens(u.total_tokens)}
              <div className="text-xs text-muted-foreground">
                {u.prompt_tokens} in / {u.completion_tokens} out
              </div>
            </TD>
            <TD className="text-right tabular-nums">{formatUsd(u.cost_usd)}</TD>
            <TD className="text-right text-xs tabular-nums text-muted-foreground">
              {u.duration_ms != null ? `${Math.round(u.duration_ms)} ms` : "—"}
            </TD>
            <TD>
              {u.status === "error" ? (
                <Badge variant="destructive" title={u.error ?? undefined}>
                  error
                </Badge>
              ) : (
                <Badge variant="success">ok</Badge>
              )}
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

/** OK / error, and for a cache lookup the verdict and the score behind it —
 * the number to look at when tuning an agent's threshold. */
function UsageResult({ row }: { row: EmbeddingUsageRow }) {
  if (row.status === "error") {
    return (
      <Badge variant="destructive" title={row.error ?? undefined}>
        error
      </Badge>
    );
  }
  if (row.source === "semantic_cache" && row.detail) {
    const hit = row.detail.hit === true;
    const score = typeof row.detail.score === "number" ? row.detail.score.toFixed(3) : null;
    return (
      <Badge
        variant={hit ? "success" : "outline"}
        title={
          typeof row.detail.threshold === "number"
            ? `threshold ${row.detail.threshold}${row.detail.stored ? " · answer stored" : ""}`
            : undefined
        }
      >
        {hit ? "hit" : "miss"}
        {score ? ` ${score}` : ""}
      </Badge>
    );
  }
  return <Badge variant="success">ok</Badge>;
}

// ---- Governance --------------------------------------------------------------

const VERDICT_VARIANT: Record<string, "success" | "warning" | "destructive"> = {
  pass: "success",
  warn: "warning",
  fail: "destructive",
};

function GovernanceTab() {
  const [verdict, setVerdict] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [offset, setOffset] = React.useState(0);

  const changeFilter = (set: (v: string) => void) => (v: string) => {
    set(v);
    setOffset(0);
  };

  const reviews = useQuery({
    queryKey: ["audit", "governance", verdict, status, offset],
    queryFn: () =>
      listAuditGovernance({
        verdict: verdict || undefined,
        status: status || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    placeholderData: keepPreviousData,
  });

  const rows = reviews.data?.rows ?? [];

  return (
    <>
      <Card className="mb-4">
        <CardContent className="flex flex-wrap gap-3 p-4">
          <FilterSelect
            label="Verdict"
            value={verdict}
            onChange={changeFilter(setVerdict)}
            options={[
              { value: "", label: "All" },
              { value: "pass", label: "Pass" },
              { value: "warn", label: "Warn" },
              { value: "fail", label: "Fail" },
            ]}
          />
          <FilterSelect
            label="Status"
            value={status}
            onChange={changeFilter(setStatus)}
            options={[
              { value: "", label: "All" },
              { value: "complete", label: "Complete" },
              { value: "analyzing", label: "Analyzing" },
              { value: "queued", label: "Queued" },
              { value: "failed", label: "Failed" },
            ]}
          />
        </CardContent>
      </Card>

      {reviews.data ? (
        <TotalsBar totals={reviews.data.totals} requestsLabel="Analyzer calls" />
      ) : null}

      {reviews.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : reviews.isError ? (
        <ErrorState error={reviews.error} onRetry={() => void reviews.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No reviews match"
          hint="Every pull request that touches a governed .claude / .cursor file is reviewed and lands here."
        />
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <Table>
                <THead>
                  <TR>
                    <TH>When</TH>
                    <TH>Pull request</TH>
                    <TH>Author</TH>
                    <TH>Status</TH>
                    <TH>Verdict</TH>
                    <TH className="text-right">Findings</TH>
                    <TH>Analyzer</TH>
                    <TH className="text-right">Tokens</TH>
                    <TH className="text-right">Cost</TH>
                    <TH />
                  </TR>
                </THead>
                <TBody>
                  {rows.map((r) => (
                    <GovernanceRow key={r.id} row={r} />
                  ))}
                </TBody>
              </Table>
            </CardContent>
          </Card>
          <Pager offset={offset} total={reviews.data?.total ?? 0} onChange={setOffset} />
        </>
      )}

      <ConfigChanges />
    </>
  );
}

function GovernanceRow({ row: r }: { row: GovernanceAuditRow }) {
  const tokens =
    r.analyzer_tokens_prompt != null || r.analyzer_tokens_completion != null
      ? (r.analyzer_tokens_prompt ?? 0) + (r.analyzer_tokens_completion ?? 0)
      : null;
  return (
    <TR>
      <TD className="whitespace-nowrap text-xs text-muted-foreground">{when(r.created_at)}</TD>
      <TD>
        <div className="text-sm">
          {r.pr_url ? (
            <a
              href={r.pr_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 hover:underline"
            >
              #{r.pr_number} {r.pr_title ?? ""}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <>
              #{r.pr_number} {r.pr_title ?? ""}
            </>
          )}
        </div>
        <div className="font-mono text-xs text-muted-foreground">{r.repo_full_name}</div>
      </TD>
      <TD className="text-sm">{r.author_login ?? "—"}</TD>
      <TD>
        <Badge
          variant={r.status === "failed" ? "destructive" : "outline"}
          title={r.last_error ?? undefined}
        >
          {r.status}
        </Badge>
      </TD>
      <TD>
        {r.verdict ? (
          <Badge variant={VERDICT_VARIANT[r.verdict] ?? "secondary"}>{r.verdict}</Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TD>
      <TD className="text-right tabular-nums">
        {r.finding_count}
        {r.max_severity ? (
          <div className="text-xs text-muted-foreground">worst: {r.max_severity}</div>
        ) : null}
      </TD>
      <TD className="text-sm">
        {r.analyzer_model ? (
          <>
            <span className="font-mono text-xs">{r.analyzer_model}</span>
            {r.analyzer_calls != null ? (
              <div className="text-xs text-muted-foreground">{r.analyzer_calls} calls</div>
            ) : null}
          </>
        ) : (
          <span className="text-xs text-muted-foreground">rule-only</span>
        )}
        {r.analyzer_error ? (
          <div className="text-xs text-amber-600" title={r.analyzer_error}>
            partial — see review
          </div>
        ) : null}
      </TD>
      <TD className="text-right tabular-nums">{formatTokens(tokens)}</TD>
      <TD className="text-right tabular-nums">{formatUsd(r.analyzer_cost_usd)}</TD>
      <TD>
        <Button asChild variant="ghost" size="sm">
          <Link href={`/governance/${r.id}`}>Open</Link>
        </Button>
      </TD>
    </TR>
  );
}

/** Who changed agent configuration, flagged or not — the other half of the
 * governance trail, and the one a security lead asks about first. */
function ConfigChanges() {
  const activity = useQuery({
    queryKey: ["audit", "governance-activity"],
    queryFn: () => listGovernanceActivity(),
  });
  const rows = (activity.data ?? []).slice(0, 50);

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-base">Agent-config changes</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {activity.isLoading ? (
          <Skeleton className="m-4 h-24" />
        ) : activity.isError ? (
          <div className="p-4">
            <ErrorState error={activity.error} onRetry={() => void activity.refetch()} />
          </div>
        ) : rows.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">No governed file has changed yet.</p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>When</TH>
                <TH>Repository</TH>
                <TH>File</TH>
                <TH>Change</TH>
                <TH>Who</TH>
                <TH>PR</TH>
                <TH>Risk</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((e) => (
                <TR key={e.id}>
                  <TD className="whitespace-nowrap text-xs text-muted-foreground">
                    {when(e.occurred_at)}
                  </TD>
                  <TD className="font-mono text-xs">{e.repo_full_name}</TD>
                  <TD className="font-mono text-xs">{e.path}</TD>
                  <TD className="text-sm">{e.change_type}</TD>
                  <TD className="text-sm">{e.actor_login ?? "—"}</TD>
                  <TD className="text-sm">
                    {e.pr_review_id ? (
                      <Link href={`/governance/${e.pr_review_id}`} className="hover:underline">
                        #{e.pr_number}
                      </Link>
                    ) : e.pr_number ? (
                      `#${e.pr_number}`
                    ) : (
                      "—"
                    )}
                  </TD>
                  <TD>
                    {e.max_severity ? (
                      <Badge variant="outline">{e.max_severity}</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">none</span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
